import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Tag, Space, Badge, Tabs, Modal, Form,
  Input, InputNumber, message, Tooltip, Popconfirm, Alert
} from 'antd';
import {
  PlusOutlined, EditOutlined, CheckOutlined, CloseOutlined,
  SyncOutlined, TagsOutlined
} from '@ant-design/icons';
import { serviceCategoriesApi } from '../../services/api';

interface ServiceCategory {
  id: number;
  slug: string;
  display_name: string;
  description: string | null;
  keywords: string; // JSON string
  default_labor_rate: number;
  sort_order: number;
  is_active: number;
  icon: string | null;
  created_at: string;
  updated_at: string;
}

interface CategoryProposal {
  id: number;
  slug: string;
  display_name: string;
  description: string | null;
  icon: string | null;
  keywords: string; // JSON string
  source_text: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

const ServiceCategoryManagerPage: React.FC = () => {
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [proposals, setProposals] = useState<CategoryProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('categories');

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { categories: cats } = await serviceCategoriesApi.list();
      setCategories(cats);
    } catch (err) {
      message.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProposals = useCallback(async () => {
    setProposalsLoading(true);
    try {
      const { proposals: props } = await serviceCategoriesApi.getProposals();
      setProposals(props);
    } catch (err) {
      message.error('Failed to load proposals');
    } finally {
      setProposalsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
    loadProposals();
  }, [loadCategories, loadProposals]);

  const pendingCount = proposals.filter((p) => p.status === 'pending').length;

  const handleApprove = async (id: number) => {
    try {
      await serviceCategoriesApi.approve(id);
      message.success('Category approved and activated!');
      await Promise.all([loadCategories(), loadProposals()]);
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to approve');
    }
  };

  const handleReject = async (id: number) => {
    try {
      await serviceCategoriesApi.reject(id);
      message.success('Proposal rejected');
      loadProposals();
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to reject');
    }
  };

  const handleDeactivate = async (slug: string) => {
    try {
      await serviceCategoriesApi.deactivate(slug);
      message.success('Category deactivated');
      loadCategories();
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to deactivate');
    }
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (category: ServiceCategory) => {
    setEditingCategory(category);
    const keywords = (() => {
      try { return JSON.parse(category.keywords).join(', '); } catch { return ''; }
    })();
    form.setFieldsValue({
      ...category,
      keywords,
    });
    setModalOpen(true);
  };

  const handleModalSave = async () => {
    try {
      const values = await form.validateFields();
      // keywords is optional — guard against undefined so a blank field
      // doesn't throw a silent TypeError that leaves the modal stuck open.
      const keywords = ((values.keywords as string) || '')
        .split(',')
        .map((k: string) => k.trim())
        .filter(Boolean);

      if (editingCategory) {
        await serviceCategoriesApi.update(editingCategory.slug, { ...values, keywords });
        message.success('Category updated');
      } else {
        await serviceCategoriesApi.create({ ...values, keywords });
        message.success('Category created');
      }

      setModalOpen(false);
      form.resetFields();
      loadCategories();
    } catch (err: any) {
      if (err?.response?.data?.error) {
        message.error(err.response.data.error);
      }
    }
  };

  const categoryColumns = [
    {
      title: 'Icon',
      dataIndex: 'icon',
      width: 60,
      render: (icon: string | null) => <span style={{ fontSize: 20 }}>{icon || '🔧'}</span>,
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      render: (slug: string) => <Tag color="blue">{slug}</Tag>,
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
    },
    {
      title: 'Hourly Rate',
      dataIndex: 'default_labor_rate',
      render: (rate: number) =>
        rate > 0 ? <Tag color="green">${rate}/hr</Tag> : <Tag color="orange">Not set</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (active: number) =>
        active ? <Badge status="success" text="Active" /> : <Badge status="default" text="Inactive" />,
    },
    {
      title: 'Actions',
      render: (_: any, record: ServiceCategory) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          {record.slug !== 'other' && record.is_active === 1 && (
            <Popconfirm
              title="Deactivate this category?"
              description="It will no longer be available for estimates or work orders."
              onConfirm={() => handleDeactivate(record.slug)}
              okText="Deactivate"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger>Deactivate</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const proposalColumns = [
    {
      title: 'Icon',
      dataIndex: 'icon',
      width: 60,
      render: (icon: string | null) => <span style={{ fontSize: 20 }}>{icon || '🔧'}</span>,
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      render: (slug: string) => <Tag color="purple">{slug}</Tag>,
    },
    {
      title: 'Suggested Name',
      dataIndex: 'display_name',
    },
    {
      title: 'Triggered By',
      dataIndex: 'source_text',
      ellipsis: true,
      render: (text: string | null) => text || <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: string) => {
        const color = status === 'pending' ? 'gold' : status === 'approved' ? 'green' : 'red';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Actions',
      render: (_: any, record: CategoryProposal) =>
        record.status === 'pending' ? (
          <Space>
            <Popconfirm title="Approve this category?" onConfirm={() => handleApprove(record.id)} okText="Approve">
              <Button size="small" type="primary" icon={<CheckOutlined />}>Approve</Button>
            </Popconfirm>
            <Popconfirm title="Reject this proposal?" onConfirm={() => handleReject(record.id)} okText="Reject" okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<CloseOutlined />}>Reject</Button>
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  const tabItems = [
    {
      key: 'categories',
      label: (
        <Space>
          <TagsOutlined />
          Categories
          <Tag>{categories.filter((c) => c.is_active).length} active</Tag>
        </Space>
      ),
      children: (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
            <Alert
              type="info"
              showIcon
              message="Categories are used by the AI to classify service items. Hourly rates can be set per category as defaults."
              style={{ flex: '1 1 240px' }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Add Category
            </Button>
            <Button icon={<SyncOutlined />} onClick={loadCategories} />
          </div>
          <Table
            dataSource={categories}
            columns={categoryColumns}
            rowKey="slug"
            loading={loading}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 25 }}
          />
        </div>
      ),
    },
    {
      key: 'proposals',
      label: (
        <Space>
          AI Proposals
          {pendingCount > 0 && <Badge count={pendingCount} />}
        </Space>
      ),
      children: (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between' }}>
            <Alert
              type="warning"
              showIcon
              message="When the AI returns an unrecognized service category, it proposes it here for your review. Approve to add it to your category list."
              style={{ flex: '1 1 240px' }}
            />
            <Button icon={<SyncOutlined />} onClick={loadProposals} />
          </div>
          {pendingCount === 0 && (
            <Alert type="success" showIcon message="No pending proposals — all categories are up to date!" style={{ marginBottom: 16 }} />
          )}
          <Table
            dataSource={proposals}
            columns={proposalColumns}
            rowKey="id"
            loading={proposalsLoading}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 25 }}
          />
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Service Category Manager</h1>
        <p style={{ color: '#666', margin: '4px 0 0' }}>
          Manage service categories used by the AI estimate system. Categories determine how services are classified and priced.
        </p>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />

      <Modal
        title={editingCategory ? `Edit: ${editingCategory.display_name}` : 'Add New Category'}
        open={modalOpen}
        onOk={handleModalSave}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        okText={editingCategory ? 'Save Changes' : 'Create Category'}
        width={560}
      >
        <Form form={form} layout="vertical">
          {!editingCategory && (
            <Form.Item
              name="slug"
              label="Slug (ID)"
              rules={[
                { required: true, message: 'Slug is required' },
                { pattern: /^[a-z0-9_]+$/, message: 'Lowercase letters, numbers, and underscores only' },
              ]}
              extra="Unique identifier, lowercase with underscores (e.g., fuel, hybrid_ev)"
            >
              <Input placeholder="e.g., fuel" />
            </Form.Item>
          )}
          <Form.Item name="icon" label="Icon (emoji)" extra="Single emoji to represent this category">
            <Input placeholder="e.g., ⛽" maxLength={10} />
          </Form.Item>
          <Form.Item name="display_name" label="Display Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g., Fuel System" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Brief description of services in this category" />
          </Form.Item>
          <Form.Item
            name="default_labor_rate"
            label="Default Hourly Rate ($/hr)"
            extra="Leave at 0 to use your shop's default rate"
          >
            <InputNumber min={0} max={9999} precision={2} style={{ width: '100%' }} addonBefore="$" addonAfter="/hr" />
          </Form.Item>
          <Form.Item
            name="keywords"
            label="Detection Keywords (comma-separated)"
            extra="Keywords used to auto-detect this category from service descriptions"
          >
            <Input.TextArea rows={2} placeholder="e.g., fuel pump, fuel filter, fuel injector, evap" />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort Order" extra="Lower numbers appear first in lists">
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceCategoryManagerPage;
